import { Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { AsyncSelectOption } from "../../../../../components/AsyncSelect";
import AsyncSelectScroll from "../../../../../components/AsyncSelectScroll";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import CustomSelect, { Value } from "../../../../../components/CustomSelect";
import ReadOnlyFields from "../../../../../components/ReadOnlyFields";
import Input from "../../../../../components/Input";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useGetClaimsByUuids } from "../../../../../queries/billings/claims/use-get-claims-by-uuids";
import { useGetEobById } from "../../../../../queries/billings/eob/use-get-eob-by-id";
import { usePostPayment } from "../../../../../queries/billings/eob/use-post-payment";
import { useInsurancePayerList } from "../../../../../queries/use-insurance-payer";
import { EobClaimPaymentPaidAsEnum } from "../../../../../services";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
  showToastOnApiCall,
} from "../../../../../utils/utils";
import { PAYMENT_METHOD_OPTIONS, getEobRemainingPayment } from "./constants";
import SelectedClaimsSection, {
  SelectedClaim,
  getAllowedAmount,
  getBalance,
  getContractAdjustment,
  getOtherAdjustment,
  getPatientResponsibility,
  isCptPosted,
  mapClaimToSelectedClaim,
} from "./SelectedClaimsSection";

interface PostPaymentDrawerProps {
  disclosureHost: DisclosureHost;
}

// The EOB row the drawer was opened from. The list response already carries the
// EOB's payer under `insurancePayer`, so it is handed over here rather than
// waiting on the detail call for a value the caller already has.
export interface PostPaymentDrawerPayload {
  uuid: string;
  payerId?: string;
}

const PostPaymentDrawer = ({ disclosureHost }: PostPaymentDrawerProps) => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const eobPayload = disclosureHost.data as
    PostPaymentDrawerPayload | undefined;
  const eobUuid = eobPayload?.uuid;

  const { data: eobDetail } = useGetEobById(
    disclosureHost.opened ? eobUuid : undefined
  );
  const eobData = (eobDetail as any)?.data as any;

  const [searchInsKey, setSearchInsKey] = useDebounce<string>("");
  const [payerPagination, setPayerPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [selectedClaims, setSelectedClaims] = useState<SelectedClaim[]>([]);

  // EOB summary bar state
  const [payerId, setPayerId] = useState<string>("");
  const [payerName, setPayerName] = useState<string>("");
  const [eobDoc, setEobDoc] = useState<string>("");
  const [totalPaymentAmount, setTotalPaymentAmount] = useState<string>("");
  const [checkEftNumber, setCheckEftNumber] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [statementDate, setStatementDate] = useState<string>("");

  // Sync EOB data to local state when loaded
  const [lastSyncedUuid, setLastSyncedUuid] = useState<string>("");
  if (eobData && eobUuid && lastSyncedUuid !== eobUuid) {
    setPayerId(eobData?.insurancePayer?.payerId ?? "");
    setPayerName(eobData?.insurancePayer?.payerName ?? "");
    setEobDoc(eobData?.eobNumber ?? "");
    setTotalPaymentAmount(String(eobData?.totalPaymentAmount ?? ""));
    setCheckEftNumber(eobData?.checkEftNumber ?? "");
    setPaymentMethod(eobData?.paymentMethod ?? "");
    setStatementDate(eobData?.statementDate ?? "");
    setLastSyncedUuid(eobUuid);
  }

  // Prefer whatever the detail call returned, but fall back to the payer the
  // list row was opened with so the claim dropdown can filter straight away.
  const eobPayerId = payerId || eobPayload?.payerId || "";

  // What the EOB still has left to post, as the backend reports it. Shown in
  // the header and sent to the claim list as its `remainingBalance` filter, so
  // the dropdown only offers claims this EOB can still cover.
  const eobRemainingPayment = Number(eobData?.remainingPayment ?? 0);
  const eobRemainingBalance =
    eobRemainingPayment > 0 ? eobRemainingPayment : undefined;

  /**
   * Claims already associated with this EOB: the detail call names them, the
   * claim list call fills in the CPT lines the table needs. Reopening a saved
   * EOB therefore shows the claims it was saved with.
   */
  const { data: postedClaimsDetail } = useGetClaimsByUuids(
    Object.values(eobData?.postedClaims ?? {})
  );

  useEffect(() => {
    const claims = (postedClaimsDetail as any)?.data ?? [];
    if (disclosureHost.opened && claims.length)
      setSelectedClaims(claims.map(mapClaimToSelectedClaim));
  }, [disclosureHost.opened, postedClaimsDetail]);

  const {
    data: insurancePayerList,
    isLoading: isPayerLoading,
    isFetching: isFetchingPayer,
  } = useInsurancePayerList({
    searchKey: searchInsKey,
    xTENANTID: getXtenantId(),
    pagination: payerPagination,
  });

  const { mutateAsync: postPayment } = usePostPayment();

  const payerOptions =
    insurancePayerList?.data?.content?.map(
      (ins: { payerId: string; payerName: string }) => ({
        value: ins.payerId,
        label: ins.payerName,
      })
    ) ?? [];

  // ECRH-4700 — post a single CPT (service line). Only the clicked CPT is sent
  // in `patientProcedureCodes`, so just that line's financials are posted. The
  // CPT is then flagged posted locally; the claim flips to `posted` only once
  // every line has been posted.
  const handlePostCpt = (claimUuid: string, pcUuid: string) => {
    const claim = selectedClaims.find((c) => c.claimUuid === claimUuid);
    if (!claim) return;
    const pc = claim.patientProcedureCodes.find((p) => p.uuid === pcUuid);
    if (!pc) return;

    // Paid As is a claim-level field required by the backend enum
    // (PRIMARY/SECONDARY/TERTIARY/OTHER). Guide the user instead of failing
    // the API call. (ECRH-4700)
    if (!claim.paidAs) {
      showSnackbar({
        message: "Please select 'Paid As' for the claim before posting.",
        type: "error",
      });
      return;
    }

    const payerName = eobData?.insurancePayer?.payerName;

    const request = {
      eobUuid: eobUuid!,
      claims: [
        {
          claimUuid: claim.claimUuid,
          paidAs: claim.paidAs as EobClaimPaymentPaidAsEnum,
          ...(claim.dateOfService
            ? { dateOfService: claim.dateOfService }
            : {}),
          ...(claim.provider && claim.provider !== "-"
            ? { providerName: claim.provider }
            : {}),
          ...(payerName ? { payerName } : {}),
          ...(claim.totalCharge !== null && claim.totalCharge !== undefined
            ? { totalCharge: claim.totalCharge }
            : {}),
          ...(claim.status ? { status: claim.status } : {}),
          patientProcedureCodes: [
            {
              uuid: pc.uuid,
              insurancePaid: pc.insurancePaid,
              writeOff: pc.writeOff,
              writeOffType: pc.writeOffType,
              adjustment: pc.adjustment,
              adjustmentType: pc.adjustmentType,
              copay: pc.copay,
              coInsurance: pc.coInsurance,
              deductible: pc.deductible,
              comments: pc.comments,
              denied: pc.denied,
              // ECRH-5391 — denial code only applies to denied CPT lines.
              ...(pc.denied ? { denialCode: pc.denialCode.trim() } : {}),
              // The values this form already shows on the service line. Without
              // them the claim's procedure table has nothing to render in its
              // Patient Resp / Allowed / Contract Adj / Other Adj / Balance
              // columns. Balance is the one on screen, not the stale
              // `remainingBalance` the claim was fetched with.
              patientResponsibility: getPatientResponsibility(pc),
              allowedAmount: getAllowedAmount(pc),
              contractAdjustment: getContractAdjustment(pc),
              otherAdjustment: getOtherAdjustment(pc),
              remainingBalance: getBalance(pc),
            },
          ],
        },
      ],
    };

    showToastOnApiCall({
      apiCall: postPayment({ eobUuid: eobUuid!, request }),
      showSnackbar,
      onSave: () => {
        queryClient.invalidateQueries({ queryKey: ["get-all-eobs"] });
        queryClient.invalidateQueries({ queryKey: ["get-eob-by-id"] });
        setSelectedClaims((prev) =>
          prev.map((c) => {
            if (c.claimUuid !== claimUuid) return c;
            // CPT posting state lives on `postingStatus` alone, so reflect the
            // outcome we just posted: a denied line lands on DENIED, everything
            // else on PAID.
            const updatedPcs = c.patientProcedureCodes.map((p) =>
              p.uuid === pcUuid
                ? { ...p, postingStatus: p.denied ? "DENIED" : "PAID" }
                : p
            );
            const allPosted =
              updatedPcs.length > 0 && updatedPcs.every(isCptPosted);
            return {
              ...c,
              expanded: true,
              patientProcedureCodes: updatedPcs,
              posted: allPosted,
            };
          })
        );
      },
    });
  };

  function closeAndReset() {
    setSelectedClaims([]);
    setLastSyncedUuid("");
    disclosureHost.close();
  }

  return (
    <SideSheetDrawer
      open={disclosureHost.opened}
      onClose={closeAndReset}
      title="Post Payment Details"
      w="100vw"
      backButton
      withCloseIcon={false}
    >
      {/* EOB Summary Bar — the ERA is presented here, not entered. */}
      <ReadOnlyFields>
        <Grid container spacing={2} mb={3}>
          <Grid item xs={2}>
            <AsyncSelectScroll
              loading={isPayerLoading || isFetchingPayer}
              pagination={payerPagination}
              setPagination={setPayerPagination}
              totalElements={insurancePayerList?.data?.page?.totalElements}
              disabled
              value={
                eobPayerId || payerName
                  ? ({
                      value: eobPayerId,
                      label: payerName,
                    } as AsyncSelectOption)
                  : null
              }
              onChange={(newValue: AsyncSelectOption | null) => {
                setPayerId(newValue?.value ?? "");
              }}
              onSearch={(val) => {
                setSearchInsKey(val);
                setPayerPagination((p) => ({ ...p, page: 0 }));
              }}
              enableFilterOption
              options={payerOptions}
              label="Payer Name"
              placeholder="Select Payer"
            />
          </Grid>
          <Grid item xs={2}>
            <Input
              label="EOB Doc"
              textFieldProps={{
                placeholder: "Enter EOB Doc",
                value: eobDoc,
                disabled: true,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  setEobDoc(e.target.value),
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <Input
              label="Total Payment Amount ($)"
              textFieldProps={{
                placeholder: "Enter Amount",
                disabled: true,
                value: totalPaymentAmount,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  setTotalPaymentAmount(e.target.value),
                type: "number",
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <Input
              label="Cheque/EFT Number"
              textFieldProps={{
                placeholder: "Enter Cheque/EFT Number",
                disabled: true,
                value: checkEftNumber,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  setCheckEftNumber(e.target.value),
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <CustomSelect
              label="Payment Method"
              readOnly
              placeholder="Select Payment Method"
              options={PAYMENT_METHOD_OPTIONS}
              value={paymentMethod}
              onChange={(value: Value) => setPaymentMethod(value ?? "")}
            />
          </Grid>
          <Grid item xs={2}>
            <CustomDatePicker
              disabled
              label="EOB Statement Date"
              placeholder="MM/DD/YYYY"
              name="statementDate"
              maxDate={dayjs()}
              value={statementDate}
              onChange={(val) =>
                setStatementDate(val ? val?.toISOString() : "")
              }
            />
          </Grid>
          <Grid item xs={2}>
            <Input
              label="Remaining Balance ($)"
              textFieldProps={{
                disabled: true,
                value: getEobRemainingPayment(eobData ?? {}),
              }}
            />
          </Grid>
        </Grid>
      </ReadOnlyFields>

      {/* Selected Claims Section */}
      <SelectedClaimsSection
        selectedClaims={selectedClaims}
        setSelectedClaims={setSelectedClaims}
        onPostCpt={handlePostCpt}
        enabled={disclosureHost.opened}
        payerId={eobPayerId || undefined}
        remainingBalance={eobRemainingBalance}
      />
    </SideSheetDrawer>
  );
};

export default PostPaymentDrawer;
